import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { ApiError } from '@/api/errors'
import { safeNext } from '@/lib/url'
import { AuthLayout } from '@/layouts/AuthLayout'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { AlertTriangle, ICON_SIZE, ICON_STROKE } from '@/components/icons'

/** The door. */
export function LoginPage() {
  useDocumentTitle('Sign in')

  const { signIn } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      await signIn(email, password)
      // Validated as a same-origin path first. An unvalidated `next` is an open
      navigate(safeNext(params.get('next')), { replace: true })
    } catch (err) {
      setError(messageFor(err))
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={(e) => { void onSubmit(e) }} className="flex flex-col gap-4" noValidate>
        <header className="flex flex-col gap-1">
          <h1 className="text-h1 font-semibold text-text">Sign in</h1>
          <p className="text-sm text-muted">Paisa24 Control Panel</p>
        </header>

        {error && (
          <div
            // role="alert" so the failure is announced when it appears,.
            role="alert"
            className="flex items-start gap-2 rounded-md border border-danger-border bg-danger-bg px-3 py-2.5 text-sm text-danger"
          >
            <AlertTriangle
              size={ICON_SIZE.sm}
              strokeWidth={ICON_STROKE}
              aria-hidden
              className="mt-0.5 shrink-0"
            />
            <p>{error}</p>
          </div>
        )}

        <Input
          label="Email address"
          type="email"
          name="email"
          autoComplete="username"
          autoFocus
          required
          value={email}
          onChange={(e) => { setEmail(e.target.value) }}
          disabled={submitting}
        />

        <PasswordInput
          label="Password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => { setPassword(e.target.value) }}
          disabled={submitting}
        />

        <Button type="submit" variant="primary" fullWidth loading={submitting}>
          Sign in
        </Button>

        <p className="text-xs text-subtle">
          Forgotten your password? Ask a Super Admin to reset it for you.
        </p>
      </form>
    </AuthLayout>
  )
}

/** One sentence for every credential failure. */
function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong. Please try again.'

  if (error.code === 'ACCOUNT_LOCKED') {
    const minutes = error.retryAfterSeconds ? Math.ceil(error.retryAfterSeconds / 60) : null
    return minutes
      ? `Too many failed attempts. Try again in ${String(minutes)} minute${minutes === 1 ? '' : 's'}.`
      : 'Too many failed attempts. Try again shortly.'
  }
  if (error.code === 'RATE_LIMITED') return 'Too many attempts from this network. Try again shortly.'
  if (error.status === 0) return 'Cannot reach the server. Check your connection.'
  if (error.status >= 500) return 'Something went wrong on our side. Try again in a moment.'

  return 'Incorrect email or password.'
}
